<template>
  <LoadingContent v-if="isPending" />
  <div class="sessions-list" v-else-if="!!sessions.length">
    <div v-for="sessionBlock in sessionsByMonthRef" :key="sessionBlock.month" class="sessions-list__month">
      <p class="sessions-list__month__date">{{ dayjsClient(sessionBlock.month, 'MM-YYYY').format('MMMM YYYY') }}</p>
      <div class="sessions-list__month__content">
        <SessionCard v-for="session in sessionBlock.sessions" :key="session.id" :session="session" />
      </div>
    </div>
  </div>
  <EmptyContent v-else :text="emptyText || t('session.listing.empty')">
    <template #icon>
      <CalendarIcon color="black" filled />
    </template>
  </EmptyContent>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { AdminSessionCardType } from '@/features/admin/sessions/components/session-card/session-card.vue'
import SessionCard from '@/features/admin/sessions/components/session-card/session-card.vue'
import type { SessionStatusEnumType, V2SessionType } from '@/features/sessions/types'
import { sortSessionsByDate } from '@/features/sessions/utils'
import EmptyContent from '@/features/ui/components/empty-content/empty-content.vue'
import CalendarIcon from '@/features/ui/components/icons/calendar-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'

const props = defineProps<{
  sessions: AdminSessionCardType[]
  isPending: boolean
  emptyText?: string
  order?: 'asc' | 'desc'
}>()

const { t } = useI18n()

const sessionsByMonthRef = computed(() => {
  const sessionsToBeSorted = props.sessions
  return sessionsToBeSorted
    .sort((sessionA, sessionB) => sortSessionsByDate(sessionA, sessionB, props.order))
    .reduce(
      (acc, session) => {
        const monthYear = dayjsClient(session.from).format('MM-YYYY')
        const monthYearSessions = acc.find((block) => block.month === monthYear)

        if (monthYearSessions) {
          monthYearSessions.sessions.push(session)
        } else {
          acc.push({
            month: monthYear,
            sessions: [session],
          })
        }

        return acc
      },
      [] as { month: string; sessions: (V2SessionType & { status: SessionStatusEnumType })[] }[],
    )
})
</script>

<style lang="scss">
@import './sessions-list.scss';
</style>
